import { Outlet, useLocation } from "react-router-dom";
import { Gavel } from "lucide-react";
import HeaderNav from "@/components/home/HeaderNav";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSwipeBack } from "@/hooks/useSwipeBack";
import { cn } from "@/lib/utils";

const AppLayout = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dash") || location.pathname.startsWith("/panel");

  // Enable swipe-back on mobile (except on the home page)
  useSwipeBack({ enabled: isMobile && location.pathname !== "/" });

  // Routes that should have a fixed viewport (no scroll) on desktop
  const isFixedViewport =
    isDashboard ||
    location.pathname === "/auctions" ||
    location.pathname.startsWith("/auction/") ||
    location.pathname.startsWith("/auctions/history/") ||
    location.pathname.startsWith("/market") ||
    location.pathname === "/shards";

  // Mobile pages implement their own headers/footers.
  if (isMobile) return <Outlet />;

  const showFooter = !isDashboard;

  return (
    <div
      className={cn(
        "bg-background flex flex-col",
        isFixedViewport ? "h-screen overflow-hidden" : "min-h-screen"
      )}
    >
      {/* Header */}
      <header
        className={cn(
          "z-40 bg-background/95 backdrop-blur-sm border-b border-border/50",
          isFixedViewport ? "shrink-0" : "sticky top-0"
        )}
      >
        <div className="max-w-[1600px] mx-auto px-6 py-3">
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Gavel size={18} className="text-primary" />
              </div>
              <span className="text-base font-semibold text-foreground hidden sm:block">
                OPAuctions
              </span>
            </div>
            <div className="flex items-center gap-2">
              <HeaderNav />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      {isDashboard ? (
        /* Dashboard: same max-width as other pages */
        <main className="flex-1 max-w-[1600px] w-full mx-auto px-6 py-4 overflow-hidden">
          <Outlet />
        </main>
      ) : (
        <>
          <main
            className={cn(
              "w-full",
              isFixedViewport
                ? "flex-1 max-w-[1600px] mx-auto px-6 py-4 overflow-hidden"
                : "max-w-[1600px] mx-auto px-6 py-6 pb-20 flex-1"
            )}
          >
            <Outlet />
          </main>

          {showFooter && (
            <footer
              className={cn(
                "z-30 bg-background/95 backdrop-blur-sm border-t border-border/50",
                isFixedViewport ? "shrink-0" : "fixed bottom-0 left-0 right-0"
              )}
            >
              <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <a
                    href="/imprint"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Impressum
                  </a>
                  <a
                    href="/privacy-policy"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Datenschutz
                  </a>
                  <a
                    href="/terms-of-service"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Nutzungsbedingungen
                  </a>
                </div>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  Ein Community-Projekt – nicht offiziell von OPSUCHT.
                </p>
              </div>
            </footer>
          )}
        </>
      )}
    </div>
  );
};

export default AppLayout;
